import moment from "moment";
import {
  MembershipSchema,
  MessageSchema,
  PreferenceSchema,
  ResourceBookingSchema,
  ResourceCategoryTypes,
  ResourceType,
} from "../../data/types";

const ACCEPTED_STATUSES = new Set(["approved", "completed"]);
const ACTIVE_MEMBER_STATUSES = new Set(["active", "verified", "approved"]);

export type DashboardStats = {
  todayBookings: number;
  todayResourceCount: number;
  pendingMembershipRequests: number;
  activeMembers: number;
  mostUsedTier: { name: string; count: number } | null;
  earningsThisMonth: number;
  earningsLastMonth: number;
  earningsChange: number;
  bookingRate: number;
  bookingRateLastMonth: number;
  bookingRateChange: number;
  totalRequestsThisMonth: number;
  unreadMessages: number;
  mostBookedResource: { name: string; count: number } | null;
};

const getBookingDate = (booking: ResourceBookingSchema) =>
  booking.start_time || booking.created;

const getRequestDate = (booking: ResourceBookingSchema) =>
  booking.created || booking.start_time;

const getBookingAmount = (booking: ResourceBookingSchema) =>
  booking.total_cost ?? booking.subtotal_cost ?? 0;

const isSameDay = (dateStr: string | undefined, day: moment.Moment) => {
  if (!dateStr) return false;
  return moment.utc(dateStr).isSame(moment.utc(day), "day");
};

const isInSameMonth = (
  dateStr: string | undefined,
  referenceDate: moment.Moment
) => {
  if (!dateStr) return false;
  const parsed = moment.utc(dateStr);
  return parsed.isValid() && parsed.isSame(moment.utc(referenceDate), "month");
};

const getBookingsInMonth = (
  bookings: ResourceBookingSchema[],
  referenceDate: moment.Moment,
  getDate: (booking: ResourceBookingSchema) => string | undefined
) => bookings.filter((booking) => isInSameMonth(getDate(booking), referenceDate));

const getEarningsForMonth = (
  bookings: ResourceBookingSchema[],
  referenceDate: moment.Moment
) =>
  getBookingsInMonth(bookings, referenceDate, getBookingDate)
    .filter((booking) => ACCEPTED_STATUSES.has(booking.status || ""))
    .reduce((sum, booking) => sum + getBookingAmount(booking), 0);

const getBookingRateForMonth = (
  bookings: ResourceBookingSchema[],
  referenceDate: moment.Moment
) => {
  const monthBookings = getBookingsInMonth(bookings, referenceDate, getRequestDate);
  if (monthBookings.length === 0) return 0;

  const acceptedCount = monthBookings.filter((booking) =>
    ACCEPTED_STATUSES.has(booking.status || "")
  ).length;

  return Math.round((acceptedCount / monthBookings.length) * 100);
};

const getMostBookedResource = (
  bookings: ResourceBookingSchema[],
  referenceDate: moment.Moment
) => {
  const counts = new Map<string, number>();

  getBookingsInMonth(bookings, referenceDate, getBookingDate).forEach((booking) => {
    const name = booking.resource_name?.trim();
    if (!name) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });

  let mostBooked: { name: string; count: number } | null = null;
  counts.forEach((count, name) => {
    if (!mostBooked || count > mostBooked.count) {
      mostBooked = { name, count };
    }
  });

  return mostBooked;
};

const getMostUsedTier = (memberships: MembershipSchema[]) => {
  const counts = new Map<string, number>();

  memberships
    .filter((membership) => ACTIVE_MEMBER_STATUSES.has(membership.status || ""))
    .forEach((membership) => {
      const name = (membership.plan_name || membership.plan_id || "").trim();
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });

  let mostUsedTier: { name: string; count: number } | null = null;
  counts.forEach((count, name) => {
    if (!mostUsedTier || count > mostUsedTier.count) {
      mostUsedTier = { name, count };
    }
  });

  return mostUsedTier;
};

const getPendingMembershipRequests = (
  memberships: MembershipSchema[],
  membershipRequests: MembershipSchema[]
) => {
  const pendingFromMemberships = memberships.filter(
    (membership) => membership.status === "pending"
  ).length;

  return Math.max(membershipRequests.length, pendingFromMemberships);
};

export const computeDashboardStats = (
  bookings: ResourceBookingSchema[] = [],
  messages: MessageSchema[] = [],
  memberships: MembershipSchema[] = [],
  membershipRequests: MembershipSchema[] = [],
  referenceDate: Date = new Date()
): DashboardStats => {
  const today = moment(referenceDate).startOf("day");
  const currentMonth = moment(referenceDate);
  const lastMonth = moment(referenceDate).subtract(1, "month");

  const todayBookingsList = bookings.filter((booking) =>
    isSameDay(getBookingDate(booking), today)
  );
  const todayResourceIds = new Set(
    todayBookingsList
      .map((booking) => booking.resource_id || booking.resource_name)
      .filter(Boolean)
  );

  const earningsThisMonth = getEarningsForMonth(bookings, currentMonth);
  const earningsLastMonth = getEarningsForMonth(bookings, lastMonth);
  const bookingRate = getBookingRateForMonth(bookings, currentMonth);
  const bookingRateLastMonth = getBookingRateForMonth(bookings, lastMonth);

  const unreadMessages = messages.filter(
    (message) => message.type === "received" && message.read_status === false
  ).length;

  const activeMembers = memberships.filter((membership) =>
    ACTIVE_MEMBER_STATUSES.has(membership.status || "")
  ).length;

  return {
    todayBookings: todayBookingsList.length,
    todayResourceCount: todayResourceIds.size,
    pendingMembershipRequests: getPendingMembershipRequests(
      memberships,
      membershipRequests
    ),
    activeMembers,
    mostUsedTier: getMostUsedTier(memberships),
    earningsThisMonth,
    earningsLastMonth,
    earningsChange: earningsThisMonth - earningsLastMonth,
    bookingRate,
    bookingRateLastMonth,
    bookingRateChange: bookingRate - bookingRateLastMonth,
    totalRequestsThisMonth: getBookingsInMonth(
      bookings,
      currentMonth,
      getRequestDate
    ).length,
    unreadMessages,
    mostBookedResource: getMostBookedResource(bookings, currentMonth),
  };
};

const DASHBOARD_TAB_LABELS: Record<ResourceType, string> = {
  kitchen: "Kitchen Spaces",
  farm: "Fresh food",
  documentation: "Documentations",
  training: "Culinary Training",
  funding: "Funding",
  expert: "Industry Experts",
};

type DashboardResourceListing = {
  resource_type?: ResourceType;
};

/** Treat listings without resource_type as kitchens so legacy records still count. */
const getListedResourceType = (resource: DashboardResourceListing): ResourceType =>
  resource.resource_type || "kitchen";

/** Kitchen is offered unless Resources to Offer has explicitly turned it off. */
export const isKitchenOffered = (preferences?: PreferenceSchema): boolean =>
  preferences?.kitchen !== false;

const RESOURCE_PAGE_TAB_LABELS: Record<ResourceType, string> = {
  kitchen: "Local Kitchen Spaces",
  farm: "Local Fresh Food",
  documentation: "Required Documentation",
  training: "Culinary Training",
  expert: "Industry Experts",
  funding: "Funding Opportunities",
};

/**
 * Build Resources / Payments category tabs from Settings → Resources to Offer.
 * Kitchen follows the offer flag only so an empty kitchen type can still be added.
 */
export const getOfferedResourcePageOptions = (
  preferences?: PreferenceSchema
): ResourceCategoryTypes[] => {
  const resourceOptions: ResourceCategoryTypes[] = [];

  if (isKitchenOffered(preferences)) {
    resourceOptions.push({
      name: RESOURCE_PAGE_TAB_LABELS.kitchen,
      type: "kitchen",
    });
  }
  if (preferences?.farm) {
    resourceOptions.push({
      name: RESOURCE_PAGE_TAB_LABELS.farm,
      type: "farm",
    });
  }
  if (preferences?.documentation) {
    resourceOptions.push({
      name: RESOURCE_PAGE_TAB_LABELS.documentation,
      type: "documentation",
    });
  }
  if (preferences?.training) {
    resourceOptions.push({
      name: RESOURCE_PAGE_TAB_LABELS.training,
      type: "training",
    });
  }
  if (preferences?.expert) {
    resourceOptions.push({
      name: RESOURCE_PAGE_TAB_LABELS.expert,
      type: "expert",
    });
  }
  if (preferences?.funding) {
    resourceOptions.push({
      name: RESOURCE_PAGE_TAB_LABELS.funding,
      type: "funding",
    });
  }

  return resourceOptions;
};

/**
 * Include Kitchen Spaces when it is offered and the community is empty or already has kitchens.
 * Omit it when kitchen is unchecked, or when other resource types exist and none are kitchen.
 */
export const shouldIncludeKitchenDashboardTab = (
  preferences?: PreferenceSchema,
  resources?: DashboardResourceListing[]
): boolean => {
  if (!isKitchenOffered(preferences)) {
    return false;
  }
  if (!resources || resources.length === 0) {
    return true;
  }
  return resources.some((resource) => getListedResourceType(resource) === "kitchen");
};

/** Build dashboard category tabs from Resources to Offer and the community resource list. */
export const getDashboardResourceOptions = (
  preferences?: PreferenceSchema,
  resources?: DashboardResourceListing[]
): ResourceCategoryTypes[] => {
  const resourceOptions: ResourceCategoryTypes[] = [];

  if (shouldIncludeKitchenDashboardTab(preferences, resources)) {
    resourceOptions.push({
      name: DASHBOARD_TAB_LABELS.kitchen,
      type: "kitchen",
    });
  }

  if (preferences?.farm) {
    resourceOptions.push({
      name: DASHBOARD_TAB_LABELS.farm,
      type: "farm",
    });
  }
  if (preferences?.documentation) {
    resourceOptions.push({
      name: DASHBOARD_TAB_LABELS.documentation,
      type: "documentation",
    });
  }
  if (preferences?.training) {
    resourceOptions.push({
      name: DASHBOARD_TAB_LABELS.training,
      type: "training",
    });
  }
  if (preferences?.funding) {
    resourceOptions.push({
      name: DASHBOARD_TAB_LABELS.funding,
      type: "funding",
    });
  }
  if (preferences?.expert) {
    resourceOptions.push({
      name: DASHBOARD_TAB_LABELS.expert,
      type: "expert",
    });
  }

  return resourceOptions;
};
